/**
 * Input Component
 * Reusable input field with label, error handling, and password visibility toggle
 */

import type { InputHTMLAttributes, ChangeEvent } from 'react';
import { forwardRef, useState } from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, helperText, id, type, onChange, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const [showPassword, setShowPassword] = useState(false);

    const isPasswordType = type === 'password';
    const isSearchType = type === 'search';
    const inputType = isPasswordType && showPassword ? 'text' : type;
    const hasValue = typeof props.value === 'string' && props.value.length > 0;
    const showClearButton = isSearchType && hasValue && !props.disabled;

    const handleClear = () => {
      if (onChange) {
        const event = {
          target: { value: '' },
          currentTarget: { value: '' },
        } as ChangeEvent<HTMLInputElement>;
        onChange(event);
      }
    };

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-body mb-1"
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            onChange={onChange}
            className={cn(
              'block w-full rounded-md border-strong shadow-sm focus:border-focus focus:ring-blue-500 text-sm text-heading bg-surface',
              'px-3 py-2 border',
              error && 'border-red-300 dark:border-red-700 focus:border-red-500 focus:ring-red-500',
              props.disabled && 'bg-surface-secondary cursor-not-allowed',
              (isPasswordType || showClearButton) && 'pr-10',
              className
            )}
            {...props}
          />
          {isPasswordType && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-meta hover:text-body"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          )}
          {showClearButton && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-meta hover:text-body"
              onClick={handleClear}
              tabIndex={-1}
              aria-label="Clear search"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1 text-sm text-meta">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
